import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getErrorMessage } from '@/services/api';
import type { AfterAcceptanceContext, PublicQuoteData } from './PublicQuote';
import { browser, purchaseAfterAcceptance } from './PurchasePanel';
import { PurchaseApiContext, apiGet, apiPost, type PurchaseApi } from './purchaseApi';
import {
  applyTheme,
  closeMiniApp,
  hasTelegramTheme,
  openExternalLink,
  readTelegramLaunch,
  startTelegramWebApp,
} from './telegramWebApp';

/**
 * The one details form for chat journeys: a Telegram Mini App today, a plain
 * link for WhatsApp tomorrow, a web view in the app later.
 *
 * `/m/form/:token` -- the token is signed server-side for ONE quote, ONE
 * chosen option and ONE chat identity, and lasts 30 minutes. The page accepts
 * that option (the public `accept`, from this device), then shows the same
 * purchase panel as the public quote page, pointed at the token's wrapper
 * endpoints. PAN, Aadhaar, engine and chassis numbers are typed only here.
 */

export const CHANNEL_FORM_API = '/policy/api/channels/form';

export interface ChannelFormMeta {
  chosen_quote: string;
  broker_name: string;
}

export type ChannelFormQuote = PublicQuoteData & { channel_form: ChannelFormMeta };

const EXPIRED = 'This link has expired. Go back to the chat and tap “📝 Complete details” for a new one.';

interface FormError {
  message: string;
  /** False for an expired link: only a new link from the chat helps, so no "Try again". */
  retryable: boolean;
}

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : (err as { response?: { status?: number } })?.response?.status;
}

/** In Telegram, the page paints in the client's own colours (`--tg-*`, set by `applyTheme`). */
const TELEGRAM_COLOURS = { backgroundColor: 'var(--tg-bg-color)', color: 'var(--tg-text-color)' };

function Shell({ title, themed = false, children }: { title: string; themed?: boolean; children: ReactNode }) {
  return (
    <main
      data-telegram-theme={themed ? 'true' : undefined}
      style={themed ? TELEGRAM_COLOURS : undefined}
      className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
    >
      <div className="max-w-lg mx-auto space-y-4">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white" style={themed ? { color: 'inherit' } : undefined}>
          {title}
        </h1>
        {children}
        <p className="text-xs text-gray-500">
          Your details go only to your insurance advisor and the insurer. Never send them in the chat.
        </p>
      </div>
    </main>
  );
}

export function ChannelFormPage() {
  const { token = '' } = useParams<{ token: string }>();
  const launch = useMemo(() => readTelegramLaunch(window.location.hash), []);
  const base = `${CHANNEL_FORM_API}/${encodeURIComponent(token)}`;
  const api = useMemo<PurchaseApi>(
    () => ({
      base: () => base,
      headers: (launch.initData ? { 'X-Telegram-Init-Data': launch.initData } : {}) as Record<string, string>,
      openPayment: (url: string) => {
        if (openExternalLink(url)) closeMiniApp();
        else browser.go(url);
      },
    }),
    [base, launch.initData]
  );
  const [quote, setQuote] = useState<ChannelFormQuote | null>(null);
  const [error, setError] = useState<FormError | null>(null);
  const acceptTried = useRef(false);

  const fail = useCallback((err: unknown) => {
    const expired = statusOf(err) === 410;
    setError({ message: expired ? EXPIRED : getErrorMessage(err), retryable: !expired });
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await apiGet(api, `${base}/`);
      setQuote(res.data as ChannelFormQuote);
      setError(null);
    } catch (err) {
      fail(err);
    }
  }, [api, base, fail]);

  useEffect(() => {
    if (launch.initData) {
      applyTheme(launch.themeParams);
      startTelegramWebApp();
    }
    void load();
  }, [load, launch]);

  useEffect(() => {
    if (!quote || acceptTried.current) return;
    const chosen = quote.channel_form.chosen_quote;
    if (quote.acceptance?.quote === chosen) return;
    acceptTried.current = true;
    const meta = quote.channel_form;
    apiPost(api, `${base}/accept/`, { quote: chosen })
      .then((res) => setQuote({ ...(res.data as PublicQuoteData), channel_form: meta }))
      .catch(fail);
  }, [quote, api, base, fail]);

  // A failed load or accept (network, a 5xx, a 400) is not the end: start again from the load, and
  // let the accept run once more. Without this the page stayed on the error until a manual refresh.
  const retry = () => {
    acceptTried.current = false;
    setError(null);
    setQuote(null);
    void load();
  };

  // Never 'Your details': that is the purchase panel's own heading once the form opens.
  const title = quote?.channel_form.broker_name || 'Insurance details';
  const themed = Boolean(launch.initData) && hasTelegramTheme(launch.themeParams);
  if (error) {
    return (
      <Shell title={title} themed={themed}>
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error.message}</p>
        {error.retryable && (
          <button
            type="button"
            onClick={retry}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Try again
          </button>
        )}
      </Shell>
    );
  }
  if (!quote || quote.acceptance?.quote !== quote.channel_form.chosen_quote) {
    return (
      <Shell title={title} themed={themed}>
        <p role="status" className="text-sm text-gray-600">Opening your form…</p>
      </Shell>
    );
  }
  const accepted = quote.quotes.find((option) => option.id === quote.acceptance?.quote);
  if (!accepted) {
    return (
      <Shell title={title} themed={themed}>
        <p role="alert" className="text-sm text-red-700">
          This option is no longer available. Go back to the chat and choose again.
        </p>
      </Shell>
    );
  }
  const meta = quote.channel_form;
  const ctx: AfterAcceptanceContext = {
    acceptedOption: accepted,
    state: quote.customer_state,
    locked: Boolean(quote.acceptance?.payment_declared),
    quote,
    token,
    replaceQuote: (data: PublicQuoteData) => setQuote({ ...data, channel_form: meta }),
    reload: load,
  };
  const panel = purchaseAfterAcceptance(ctx);
  return (
    <Shell title={title} themed={themed}>
      <PurchaseApiContext.Provider value={api}>
        {panel ?? (
          <p role="status" className="text-sm text-gray-700 dark:text-gray-200">
            {quote.status === 'completed'
              ? 'Your policy is issued. We are sending it to your chat.'
              : 'Your advisor will send you a payment link.'}
          </p>
        )}
      </PurchaseApiContext.Provider>
    </Shell>
  );
}
